import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';
import {
  FaBars,
  FaBell,
  FaBriefcase,
  FaBuilding,
  FaFileAlt,
  FaHome,
  FaInbox,
  FaSearch,
  FaShieldAlt,
  FaSignOutAlt,
  FaTimes,
  FaUserCircle,
  FaUsers,
  FaUserTie
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { admin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: FaHome },
    { name: 'Users', href: '/users', icon: FaUsers },
    { name: 'Recruiters', href: '/recruiters', icon: FaUserTie },
    { name: 'Employers', href: '/employers', icon: FaBuilding },
    { name: 'Jobs', href: '/jobs', icon: FaBriefcase },
    { name: 'Applications', href: '/applications', icon: FaFileAlt },
    { name: 'Bulk Requests', href: '/bulk-requests', icon: FaInbox },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActivePage = (href) => {
    return location.pathname === href;
  };

  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 40
      }
    },
    closed: {
      x: "-100%",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 40
      }
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-luxury-50 via-luxury-50 to-luxury-100">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        variants={sidebarVariants}
        animate={sidebarOpen ? "open" : "open"}
        className="fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-luxury-900 to-luxury-800 shadow-luxury-lg lg:relative lg:translate-x-0 lg:z-0"
      >
        <div className="flex h-full flex-col">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between px-6 py-6 border-b border-luxury-700/50">
            <motion.div 
              className="flex items-center space-x-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="h-10 w-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-glow">
                <FaShieldAlt className="h-5 w-5 text-white" />
              </div>
              <div>
                <motion.h1 
                  className="text-white font-bold text-xl tracking-tight"
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  jobzentro
                </motion.h1>
                <motion.p 
                  className="text-primary-400 text-sm font-medium"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  Super Admin
                </motion.p>
              </div>
            </motion.div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-luxury-400 hover:text-white transition-colors duration-200"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-8 space-y-2 overflow-y-auto">
            {navigation.map((item, index) => {
              const Icon = item.icon;
              const isActive = isActivePage(item.href);
              
              return (
                <motion.button
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  whileHover={{ 
                    scale: 1.03,
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center px-5 py-3.5 text-sm font-medium rounded-xl transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-primary-600/30 to-primary-500/20 text-white shadow-md border-l-4 border-primary-400'
                      : 'text-luxury-300 hover:text-white hover:bg-luxury-700/50'
                  }`}
                >
                  <Icon className={`mr-3 h-5 w-5 ${isActive ? 'text-primary-400' : ''}`} />
                  {item.name}
                </motion.button>
              );
            })}
          </nav>

          {/* Sidebar Footer */}
          <div className="border-t border-luxury-700/50 px-4 py-6">
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              whileHover={{ 
                scale: 1.03,
                backgroundColor: 'rgba(255,255,255,0.1)',
                transition: { duration: 0.2 }
              }}
              whileTap={{ scale: 0.98 }}
              onClick={handleLogout}
              className="flex items-center w-full px-5 py-3.5 text-sm font-medium text-luxury-300 hover:text-white hover:bg-luxury-700/50 rounded-xl transition-all duration-300"
            >
              <FaSignOutAlt className="mr-3 h-5 w-5" />
              Sign out
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <motion.header 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white/80 backdrop-blur-md border-b border-luxury-200 shadow-sm px-4 py-4 sm:px-6 lg:px-8"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile menu button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-luxury-600 hover:text-luxury-900 transition-colors duration-200"
              >
                <FaBars className="h-6 w-6" />
              </button>

              {/* Search bar */}
              <div className="hidden sm:flex items-center relative">
                <FaSearch className="absolute left-1 top-1/2 transform -translate-y-1/2 text-luxury-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-12 pr-4 py-2.5 w-72 bg-luxury-50/80 border border-luxury-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 focus:shadow-md"
                />
              </div>
            </div>

            <div className="flex items-center space-x-5">
              {/* Notifications */}
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="relative p-2 text-luxury-600 hover:text-primary-600 transition-colors duration-200"
                >
                  <FaBell className="h-5 w-5" />
                  <motion.span 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-0 right-0.5 h-2.5 w-2.5 bg-danger-500 rounded-full shadow-md"
                  ></motion.span>
                </motion.button>
                
                {/* Notification dropdown would go here */}
              </div>

              {/* User menu */}
              <div className="flex items-center space-x-3">
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-semibold text-luxury-900">
                    {admin?.name || 'Super Admin'}
                  </p>
                  <p className="text-xs text-luxury-500">{admin?.email || 'admin@jobzentro.com'}</p>
                </div>
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="h-10 w-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center shadow-md"
                >
                  <FaUserCircle className="h-6 w-6 text-white" />
                </motion.div>
              </div>
            </div>
          </div>
        </motion.header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gradient-to-br from-luxury-50 via-luxury-50 to-luxury-100">
          <div className="px-4 py-6 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {children}
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 